import React, { useMemo, useCallback, useEffect } from 'react';
import RowHighlightColorMenu from './RowHighlightColorMenu';
import type { PipingSystem } from '../types';
import { EXPORT_ROW_HIGHLIGHT_OPTIONS } from '../lib/rowHighlights';
import type {
  ExportRowHighlightColor,
  ExportRowHighlightMap,
} from '../lib/rowHighlights';

interface SelectedRowsHighlightColorMenuProps {
  systems: PipingSystem[];
  selected: Set<string>;
  rowHighlightColors: ExportRowHighlightMap;
  compact?: boolean;
  buttonRef?: React.Ref<HTMLButtonElement>;
  onChange?: (systemName: string, color: ExportRowHighlightColor | null) => void;
  onReset?: () => void;
}

function isEditableEventTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName.toLowerCase();
  return target.isContentEditable || tagName === 'input' || tagName === 'textarea' || tagName === 'select';
}

function summarizeSelectedHighlightColors(
  systemNames: string[],
  rowHighlightColors: ExportRowHighlightMap
): {
  color: ExportRowHighlightColor | null;
  hasHighlight: boolean;
  mixed: boolean;
} {
  let firstColor: ExportRowHighlightColor | null | undefined;
  let hasHighlight = false;
  let mixed = false;

  systemNames.forEach((systemName) => {
    const color = rowHighlightColors[systemName] ?? null;
    hasHighlight = hasHighlight || Boolean(color);

    if (firstColor === undefined) {
      firstColor = color;
      return;
    }

    if (firstColor !== color) {
      mixed = true;
    }
  });

  return {
    color: !mixed && firstColor ? firstColor : null,
    hasHighlight,
    mixed,
  };
}

export default function SelectedRowsHighlightColorMenu({
  systems,
  selected,
  rowHighlightColors,
  compact = false,
  buttonRef,
  onChange,
  onReset,
}: SelectedRowsHighlightColorMenuProps) {
  const selectedSystemNames = useMemo(
    () => systems
      .filter((system) => selected.has(system.systemName))
      .map((system) => system.systemName),
    [selected, systems]
  );
  const selectedHighlightSummary = useMemo(
    () => summarizeSelectedHighlightColors(selectedSystemNames, rowHighlightColors),
    [rowHighlightColors, selectedSystemNames]
  );
  const hasAnyHighlight = useMemo(
    () => Object.keys(rowHighlightColors).length > 0,
    [rowHighlightColors]
  );
  const canReset = hasAnyHighlight && Boolean(onReset);
  const handleChange = useCallback((color: ExportRowHighlightColor | null) => {
    selectedSystemNames.forEach((systemName) => {
      onChange?.(systemName, color);
    });
  }, [onChange, selectedSystemNames]);
  const buttonLabel = useMemo(() => {
    if (selectedSystemNames.length === 0) {
      return canReset
        ? 'Reset row highlight colors'
        : 'Select rows before marking row color';
    }

    if (selectedHighlightSummary.mixed) {
      return 'Change mixed selected row highlight colors';
    }

    return selectedHighlightSummary.color
      ? 'Change selected row highlight color'
      : 'Set selected row highlight color';
  }, [canReset, selectedHighlightSummary, selectedSystemNames.length]);

  useEffect(() => {
    if (!onChange || selectedSystemNames.length === 0) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || isEditableEventTarget(event.target)) {
        return;
      }

      const color = event.key === '0'
        ? null
        : EXPORT_ROW_HIGHLIGHT_OPTIONS.find((option) => option.shortcutKey === event.key)?.id;
      if (color === undefined) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      handleChange(color);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleChange, onChange, selectedSystemNames.length]);

  if (!onChange) {
    return null;
  }

  return (
    <RowHighlightColorMenu
      buttonLabel={buttonLabel}
      clearLabel="Clear selected row highlight color"
      menuLabel="Row highlight colors"
      menuItemLabel={(colorLabel) => `Highlight selected rows ${colorLabel}`}
      resetConfirmMessage="Reset all row highlight colors for this document?"
      resetLabel="Reset all row highlight colors"
      value={selectedHighlightSummary.color}
      showClear={selectedHighlightSummary.hasHighlight}
      showReset={canReset}
      disabled={selectedSystemNames.length === 0 && !canReset}
      colorOptionsDisabled={selectedSystemNames.length === 0}
      compact={compact}
      buttonRef={buttonRef}
      onChange={handleChange}
      onReset={onReset}
    />
  );
}
